import { create } from "zustand";
import axiosInstance from "../lib/axios";
import toast from "react-hot-toast";
import { io } from "socket.io-client";

const BASE_URL =
  import.meta.env.MODE === "development" ? "http://localhost:5001" : "/";

export const useAuthStore = create((set, get) => ({
  authUser: null,
  isAuthenticated: false,
  isSigningUp: false,
  isLoggingIn: false,
  isLoading: false,
  isUpdatingProfile: false,
  isCheckingAuth: true,
  onlineUsers: [],
  socket: null,
  pointHistory: [],
  hasLoggedOut: false, // Add flag to track explicit logouts

  checkAuth: async () => {
    // Skip auth check if user has explicitly logged out
    if (get().hasLoggedOut) {
      set({ isCheckingAuth: false });
      return;
    }

    try {
      const res = await axiosInstance.get("/auth/check");
      set({ 
        authUser: res.data,
        hasLoggedOut: false // Reset flag on successful auth
      });
      get().connectSocket();
    } catch (error) {
      // Don't log expected 401 errors
      if (!(error.response?.status === 401)) {
        console.log("Error in checkAuth:", error);
      }
      set({ authUser: null });
    } finally {
      set({ isCheckingAuth: false });
    }
  },

  signup: async (data) => {
    set({ isSigningUp: true, hasLoggedOut: false }); // Reset logout flag on signup attempt
    try {
      const res = await axiosInstance.post("/auth/signup", data);
      set({ authUser: res.data });
      set({ isAuthenticated: true });
      toast.success("Account created successfully");
      get().connectSocket();
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      set({ isSigningUp: false });
    }
  },

  verifyEmail: async (code) => {
    set({ isLoading: true, error: null, hasLoggedOut: false }); // Reset logout flag
    try {
      const response = await axiosInstance.post(`/auth/verify-email`, { code });
      set({
        user: response.data.user,
        isAuthenticated: true,
        isLoading: false,
      });
      return response.data;
    } catch (error) {
      set({
        error: error.response.data.message || "Error verifying email",
        isLoading: false,
      });
      throw error;
    }
  },

  resendVerificationEmail: async (email) => {
    set({ isLoading: true, error: null });
    try {
      await axiosInstance.post(
        `/auth/resend-verification-email?email=${email}`,
      );
    } catch (error) {
      toast.error("Failed to resend verification email");
      if (!(error.response?.status === 401)) {
        console.log("error in resendVerificationEmail:", error);
      }
    } finally {
      set({ isLoading: false });
    }
  },

  forgotPassword: async (email) => {
    set({ isLoading: true, hasLoggedOut: false }); // Reset logout flag
    try {
      const response = await axiosInstance.post(`/auth/forgot-password`, {
        email,
      });
      set({ message: response.data.message, isLoading: false });
    } catch (error) {
      set({
        isLoading: false,
        error:
          error.response.data.message || "Error sending reset password email",
      });
      throw error;
    }
  },
  resetPassword: async (token, password) => {
    set({ isLoading: true, error: null, hasLoggedOut: false }); // Reset logout flag
    try {
      const response = await axiosInstance.post(
        `/auth/reset-password/${token}`,
        {
          password,
        },
      );
      set({ message: response.data.message, isLoading: false });
    } catch (error) {
      set({
        isLoading: false,
        error: error.response.data.message || "Error resetting password",
      });
      throw error;
    }
  },

  login: async (data) => {
    set({ isLoggingIn: true, hasLoggedOut: false }); // Reset logout flag on login attempt
    try {
      const res = await axiosInstance.post("/auth/login", data);
      set({ authUser: res.data });
      toast.success("Logged in successfully");

      get().connectSocket();
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      set({ isLoggingIn: false });
    }
  },

  logout: async () => {
    // Set hasLoggedOut flag immediately to prevent API calls
    set({ hasLoggedOut: true });
    
    try {
      // Disconnect socket first to prevent reconnection attempts
      get().disconnectSocket();
      
      // Then try to logout on server
      await axiosInstance.post("/auth/logout");
      
      // Clear user state
      set({ authUser: null });
    } catch (error) {
      // Don't show error toast on logout failures - they're usually just auth issues
      if (!(error.response?.status === 401)) {
        toast.error(error.response?.data?.message || "Error during logout");
      }
    }
  },

  updateProfile: async (data) => {
    set({ isUpdatingProfile: true });
    try {
      const res = await axiosInstance.put("/auth/update-profile", data);
      set({ authUser: res.data });
      toast.success("Profile updated successfully");
    } catch (error) {
      // Don't log expected 401 errors
      if (!(error.response?.status === 401)) {
        console.log("error in update profile:", error);
      }
      toast.error(error.response?.data?.message || "Failed to update profile");
    } finally {
      set({ isUpdatingProfile: false });
    }
  },

  getPointHistory: async () => {
    // Skip if logged out
    if (get().hasLoggedOut) return;
    
    try {
      const res = await axiosInstance.get("/point/history");
      set({ pointHistory: res.data });
    } catch (error) {
      // Don't log expected 401 errors
      if (!(error.response?.status === 401)) {
        console.log("error in getPointHistory:", error);
      }
      set({ pointHistory: [] });
    }
  },

  connectSocket: () => {
    // Skip if logged out
    if (get().hasLoggedOut) return;
    
    const { authUser } = get();
    if (!authUser || get().socket?.connected) return;

    const socket = io(BASE_URL, {
      query: {
        userId: authUser._id,
      },
    });

    set({ socket }); // Save socket first

    socket.on("connect", () => {
      console.log("WebSocket connected");
    });

    socket.on("disconnect", () => {
      console.warn("WebSocket disconnected");
      set({ socket: null }); // Clear socket on disconnect
    });

    socket.on("getOnlineUsers", (userIds) => {
      set({ onlineUsers: userIds });
    });

    socket.connect(); // Connect after setting
  },

  disconnectSocket: () => {
    const socket = get().socket;
    if (socket) {
      try {
        if (socket.connected) socket.disconnect();
      } catch (error) {
        console.warn("Error disconnecting socket:", error);
      }
    }
    set({ socket: null });
  },

  // Add method to reset logout flag (for login page)
  resetLogoutFlag: () => {
    set({ hasLoggedOut: false });
  }
}));